import * as React from "react";
import styled, { css } from "styled-components";

import { Column, Margins, Row, SmallHeading } from "@ui/Atoms";
import { rem } from "@ui/helpers";

import { Markdown } from "@containers/Markdown";

interface TextField {
  text: string;
}

export interface PrismicWhatsIncludedContent {
  primary: {
    content_body: TextField[];
    whats_included_image: {
      url: string;
    };
    whats_included_title: [TextField];
  };
}

interface Props {
  data: PrismicWhatsIncludedContent[];
  small?: boolean;
  hasSeparator?: boolean;
  isEOT?: boolean;
  isCleaningWarranty?: boolean;
}

export const WhatsIncludedContent = (props: Props): React.ReactElement => (
  <React.Fragment>
    {props.data?.map((item: PrismicWhatsIncludedContent, i: number) => {
      const MARGIN = props.data.length - 1 === i ? null : "base_x2";

      const titleText = item?.primary?.whats_included_title[0]?.text;
      const markdownText = item?.primary?.content_body?.reduce(
        (total, curr) => total + "\n " + curr.text,
        "",
      );
      const image = item?.primary?.whats_included_image?.url;

      const COLUMNS_CONFIG = {
        xs: 12,
        lg:
          props.isEOT ||
          (props.isCleaningWarranty && props.data.length - 1 === i)
            ? 12
            : 6,
      };

      return (
        <Margins xs={[null, MARGIN, null, null]} key={titleText}>
          <StyledRow
            as="section"
            hasSeparator={props.hasSeparator}
            isReversed={props.isCleaningWarranty && i % 2 !== 0}
          >
            <StyledColumn {...COLUMNS_CONFIG}>
              <div>
                <Margins xs={[null, "base", null, null]}>
                  <SmallHeading>{titleText}</SmallHeading>
                </Margins>
                <Markdown>{markdownText}</Markdown>
              </div>
            </StyledColumn>
            {image && (
              <ImageColumn {...COLUMNS_CONFIG}>
                <img src={image} alt={titleText} />
              </ImageColumn>
            )}
          </StyledRow>
        </Margins>
      );
    })}
  </React.Fragment>
);

const StyledRow = styled(Row)<{ hasSeparator?: boolean; isReversed?: boolean }>`
  position: relative;

  &:last-of-type {
    &:after {
      content: "";
      display: none;
    }
  }

  ${props =>
    props.hasSeparator &&
    css`
      &:after {
        content: "";
        position: absolute;
        bottom: 0;
        left: ${props => props.theme.margins.base_x2};
        height: ${rem(1)};
        width: calc(100% - ${props => props.theme.margins.base_x4});
        background: ${props => props.theme.palette.grey50};
      }
    `}

  ${props =>
    props.isReversed &&
    css`
      flex-direction: row-reverse;
    `}
`;

const StyledColumn = styled(Column)`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
`;

const ImageColumn = styled(Column)`
  display: flex;
  align-items: center;
`;
